import axios from 'axios';
import { previewLocation, previewReadOnlyError } from './studentPreview';
import { guardSessionRequest, observeSessionError } from './sessionRecovery';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Function to get CSRF token from cookies
function getCookie(name: string): string | null {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Set global axios defaults for CSRF and credentials
axios.defaults.headers.common['X-CSRFToken'] = getCookie('csrftoken');
axios.defaults.withCredentials = true;

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Update CSRF token for each request
api.interceptors.request.use(async (config) => {
  if (previewLocation() && !(/^\/api\/(?:auth\/me\/|csrf\/)$/.test(config.url || '') ||
      /^\/api\/v2\/sections\/\d+\/students\/\d+\/preview\/$/.test(config.url || ''))) {
    throw previewReadOnlyError();
  }
  if (previewLocation() && (config.method || 'get').toLowerCase() !== 'get') throw previewReadOnlyError();
  await guardSessionRequest(config);
  // Always update CSRF token from cookies
  const csrfToken = getCookie('csrftoken');
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
});
api.interceptors.response.use(response => response, error => {
  observeSessionError(error);
  return Promise.reject(error);
});

// Function to get CSRF token first
export const getCSRFToken = async () => {
  try {
    const response = await api.get('/api/csrf/');
    return response.data?.csrfToken || getCookie('csrftoken');
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
    return null;
  }
};

const beginGoogleLogin = async () => {
  const csrfToken = getCookie('csrftoken') || await getCSRFToken();
  if (!csrfToken) {
    throw new Error('Unable to start Google sign-in without a CSRF token.');
  }

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = '/social/login/google-oauth2/';
  form.hidden = true;

  const csrfInput = document.createElement('input');
  csrfInput.type = 'hidden';
  csrfInput.name = 'csrfmiddlewaretoken';
  csrfInput.value = csrfToken;
  form.appendChild(csrfInput);

  document.body.appendChild(form);
  form.submit();
};

export const authApi = {
  getMe: () => api.get('/api/auth/me/'),
  logout: () => api.post('/api/auth/logout/', {}, {
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  login: beginGoogleLogin,
};

export const coursesApi = {
  getCourses: () => api.get('/api/courses/'),
  createCourse: (data: { name: string; description?: string }) => api.post('/api/courses/', data),
  joinCourse: (classCode: string) => api.post('/api/courses/join/', { class_code: classCode }),
  getWithHooks: (courseId: number) => api.get(`/api/courses-with-hooks/${courseId}/`),
};

export const learningHooksApi = {
  getAll: () => api.get('/api/learning-hooks/'),
  getByCourse: (courseId: number) => api.get('/api/learning-hooks/', { params: { course: courseId } }),
  create: (data: any) => api.post('/api/learning-hooks/', data),
  update: (id: number, data: any) => api.put('/api/learning-hooks/', data, { params: { id } }),
  delete: (id: number) => api.delete('/api/learning-hooks/', { params: { id } }),
  startProgress: (id: number) => api.post(`/api/learning-hooks/${id}/start_progress/`),
  importStudents: (file: File, hookId: number) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/learning-hooks/${hookId}/import_students/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getStudents: (hookId: number) => api.get(`/api/learning-hooks/${hookId}/students/`),
  addStudent: (data: any) => api.post('/api/learning-hooks/add_student/', data),
  removeStudent: (studentId: number, hookId: number) => 
    api.delete(`/api/learning-hooks/${hookId}/students/${studentId}/`),
  updateStudent: (studentId: number, data: any) => 
    api.put(`/api/learning-hooks/students/${studentId}/`, data),
  transferStudents: (data: any) => api.post('/api/learning-hooks/transfer-students/', data),
  getAvailableStudents: (gradeLevel?: string, hookId?: number) => {
    const params: any = {};
    if (gradeLevel) params.grade_level = gradeLevel;
    if (hookId) params.hook_id = hookId;
    return api.get('/api/learning-hooks/available-students/', { params });
  },
  enrollExisting: (hookId: number, studentIds: number[]) => 
    api.post('/api/learning-hooks/enroll-existing/', { hook_id: hookId, student_ids: studentIds }),
};

export const studentProgressApi = {
  getAll: () => api.get('/api/student-progress/'),
  getMine: () => api.get('/api/student-progress/', { params: { student: 'me' } }),
  updateStage: (id: number, stage: string) => api.post(`/api/student-progress/${id}/update_stage/`, { stage }),
  submitEvidence: (id: number, stage: string, evidence: string) => 
    api.post(`/api/student-progress/${id}/submit_evidence/`, { stage, evidence }),
};

export const sessionsApi = {
  getActive: () => api.get('/api/sessions/active/'),
  create: (lessonId: number) => api.post('/api/sessions/', { lesson_id: lessonId }),
};

export const healthApi = {
  check: () => api.get('/api/health/'),
};

// Import endpoints
export const importApi = {
  getTemplate: () => api.get('/api/import/template/'),
  getTeacherCourses: () => api.get('/api/import/courses/'),
  importStudents: (file: File, courseId: number) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('course_id', courseId.toString());
    return api.post('/api/import/students/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Student management endpoints
export const studentManagementApi = {
  addSingleStudent: (data: any) => api.post('/api/students/add/', data),
  removeStudentFromCourse: (studentId: number, courseId: number) => 
    api.delete(`/api/students/${studentId}/courses/${courseId}/remove/`),
  updateStudentInfo: (studentId: number, data: any) => 
    api.put(`/api/students/${studentId}/update/`, data),
  getCourseStudents: (courseId: number) => 
    api.get(`/api/courses/${courseId}/students/`),
  transferStudents: (data: any) => api.post('/api/students/transfer/', data),
};

export const hookEnrollmentApi = {
  getMyEnrollments: () => api.get('/api/hook-enrollments/my_enrollments/'),
  getAvailableHooks: () => api.get('/api/hook-enrollments/available_hooks/'),
  enrollInHook: (hookId: number) => api.post('/api/hook-enrollments/', { learning_hook: hookId }),
  completeEnrollment: (enrollmentId: number) => 
    api.post(`/api/hook-enrollments/${enrollmentId}/complete/`),
  getEnrollments: () => api.get('/api/hook-enrollments/'),
};

export const tasksApi = {
  list: (params?: { hook_id?: number; stage?: string }) => api.get('/api/tasks/', { params }),
  create: (data: any) => api.post('/api/tasks/', data),
  update: (taskId: number, data: any) => api.put(`/api/tasks/${taskId}/`, data),
  delete: (taskId: number) => api.delete(`/api/tasks/${taskId}/`),
  listEvidence: (taskId: number) => api.get(`/api/tasks/${taskId}/evidence/`),
  submitEvidence: (taskId: number, data: { submission_text: string }) => api.post(`/api/tasks/${taskId}/evidence/`, data),
  setDriveTemplate: (
    taskId: number,
    data: {
      google_template_file_id?: string | null;
      google_template_name?: string | null;
      google_template_mime_type?: string | null;
    }
  ) =>
    api.post(`/api/tasks/${taskId}/set-drive-template/`, data),
  getArtifact: (taskId: number) => api.get(`/api/tasks/${taskId}/artifact/`),
};

export const googleDriveApi = {
  accessToken: () => api.get('/api/google/drive/access-token/'),
  fileAccess: (fileId: string) => api.post('/api/google/drive/file-access/', { file_id: fileId }),
  presentationSlides: (fileId: string) => api.post('/api/google/drive/presentation-slides/', { file_id: fileId }),
};

export const evidenceApi = {
  reviewQueue: (params?: { hook_id?: number; status?: string }) => api.get('/api/evidence/review-queue/', { params }),
  review: (evidenceId: number, data: { decision: 'approved' | 'needs_revision'; notes?: string }) =>
    api.post(`/api/evidence/${evidenceId}/review/`, data),
};

export const liveInstructionApi = {
  getHookSession: (hookId: number) => api.get(`/api/learning-hooks/${hookId}/live-session/`),
  startHookSession: (hookId: number, data: { level: string; mode: string; title?: string }) =>
    api.post(`/api/learning-hooks/${hookId}/live-session/start/`, data),
  endSession: (sessionId: number) => api.post(`/api/live-sessions/${sessionId}/end/`, {}),
  updateSession: (sessionId: number, data: { level?: string; mode?: string; title?: string }) =>
    api.put(`/api/live-sessions/${sessionId}/`, data),
  setInstructionSource: (sessionId: number, data: { instruction_source_id: string }) =>
    api.post(`/api/live-sessions/${sessionId}/set-instruction-source/`, data),
  listArtifacts: (sessionId: number) => api.get(`/api/live-sessions/${sessionId}/artifacts/`),
  upsertArtifact: (
    sessionId: number,
    data: { kind: string; content?: string; links?: any[]; published_state?: 'draft' | 'published' }
  ) => api.post(`/api/live-sessions/${sessionId}/artifacts/`, data),
  getSignals: (sessionId: number) => api.get(`/api/live-sessions/${sessionId}/signals/`),
  postSignal: (sessionId: number, data: { signal_type: 'good' | 'confused' | 'another_example' | 'slow_down' }) =>
    api.post(`/api/live-sessions/${sessionId}/signals/`, data),
  aiMicroAssist: (sessionId: number, data: { action_type: string; context?: any }) =>
    api.post(`/api/live-sessions/${sessionId}/ai-micro-assist/`, data),
};

export default {
  auth: authApi,
  courses: coursesApi,
  sessions: sessionsApi,
  health: healthApi,
  learningHooks: learningHooksApi,
  studentProgress: studentProgressApi,
  import: importApi,
  studentManagement: studentManagementApi,
  hookEnrollment: hookEnrollmentApi,
  tasks: tasksApi,
  googleDrive: googleDriveApi,
  evidence: evidenceApi,
  liveInstruction: liveInstructionApi,
};
